import { Delete } from "lucide-react";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { Button as AriaButton } from "react-aria-components";
import { cx } from "../cx.js";
import styles from "./PinPad.module.css";

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

/**
 * PIN-Eingabe mit großen Tasten (ARCHITEKTUR §10.3) – ohne Bildschirmtastatur bedienbar,
 * zusätzlich per Hardware-Tastatur (Ziffern, Rücktaste, Enter). Ziffern werden nie angezeigt.
 */
const ZERO = "0";

export function PinPad({
  value,
  onChange,
  onSubmit,
  length = 6,
  label,
  isDisabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: (v: string) => void;
  length?: number;
  label: string;
  isDisabled?: boolean;
}) {
  const intl = useIntl();
  const press = (d: string) => {
    if (isDisabled || value.length >= length) return;
    const next = value + d;
    onChange(next);
    if (next.length === length) onSubmit?.(next);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isDisabled || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
        return;
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") onChange(value.slice(0, -1));
      else if (e.key === "Enter" && value.length === length) onSubmit?.(value);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  return (
    <div className={styles.pad} role="group" aria-label={label}>
      <div className={styles.dots} role="status" aria-live="polite">
        <span className="omony-visually-hidden">
          {intl.formatMessage({ id: "pinpad.entered" }, { count: value.length, total: length })}
        </span>
        {Array.from({ length }, (_, i) => (
          <span key={i} aria-hidden="true" className={cx(styles.dot, i < value.length && styles.filled)} />
        ))}
      </div>
      <div className={styles.keys} dir="ltr">
        {DIGITS.map((d) => (
          <AriaButton key={d} className={styles.key} onPress={() => press(d)} isDisabled={isDisabled}>
            {d}
          </AriaButton>
        ))}
        <span />
        <AriaButton className={styles.key} onPress={() => press(ZERO)} isDisabled={isDisabled}>
          {ZERO}
        </AriaButton>
        <AriaButton
          className={cx(styles.key, styles.del)}
          onPress={() => onChange(value.slice(0, -1))}
          isDisabled={isDisabled || !value}
          aria-label={intl.formatMessage({ id: "pinpad.delete" })}
        >
          <Delete aria-hidden="true" />
        </AriaButton>
      </div>
    </div>
  );
}
